import { useMemo, useState } from 'react';

import { useAgentId, useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { agentPayoutsRepoFor } from '../lib/repositories/payouts-repo.js';
import { earningsLedgerRepoFor } from '../lib/repositories/earnings-repo.js';
import { NextPayoutCard } from '../components/next-payout-card.js';
import { summariseLedgerTotals, type EarningsLedgerEntry } from '@kilat/shared';
import type { AgentPayout } from '@kilat/shared';

import {
  PayoutsHero,
  PayoutsHeroSkeleton,
  PayoutsList,
  PayoutsListSkeleton,
  PayoutsPageShell,
  PayoutsStateCard,
} from '../features/payouts/payouts-view.js';

/**
 * /payouts — every payout batch swept from the agent's earnings ledger, with
 * its live status (pending / processing / paid / failed), gross amount, bank
 * or DuitNow method, period, tx reference, and paid date. Reads
 * `agents/{agentId}/payouts/{payoutId}` (written by the super-admin
 * `runPayoutBatch` / `executePayoutBatch` Cloud Function; settled by
 * `markPayoutPaid`). Read-only from the client.
 *
 * Per [feedback-no-dead-ends] every state ends in a clear next action:
 *   - signed-out          → "Sign in" CTA
 *   - signed-in, not agent → explicit message + become-an-agent link
 *   - loading             → skeleton hero + rows
 *   - error               → message + Retry (re-subscribes via retryNonce)
 *   - empty               → explains what produces a payout batch
 *   - populated           → hero + range/status filters + batch cards + CSV
 *
 * A dropped listener surfaces the error gate — never a silent RM 0.00 hero
 * (mirrors the earnings-ledger money-safety gate, #68).
 */
export function PayoutsPage() {
  const { user, loading: authLoading } = useAuth();
  const agentId = useAgentId();
  const t = useI18n();
  const brand = usePlatformBrand();
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const subscribe = useMemo(() => {
    if (!agentId) return null;
    const repo = agentPayoutsRepoFor(agentId);
    return (cb: (vs: AgentPayout[]) => void) =>
      repo.listSnapshot(
        (v) => {
          setLoadError(null);
          cb(v);
        },
        (err) => setLoadError(err),
      );
  }, [agentId]);

  const { data: payouts, loading } = useTrackedSnapshot<AgentPayout[]>(
    ['payouts', agentId, retryNonce],
    subscribe,
  );

  // The unpaid balance, from the SAME summariser `/earnings` uses — so the two
  // screens can never quote an agent different numbers for the same money.
  const subscribeLedger = useMemo(() => {
    if (!agentId) return null;
    const repo = earningsLedgerRepoFor(agentId);
    return (cb: (vs: EarningsLedgerEntry[]) => void) => repo.listSnapshot(cb, () => cb([]));
  }, [agentId]);
  const { data: ledger } = useTrackedSnapshot<EarningsLedgerEntry[]>(
    ['payouts-ledger', agentId],
    subscribeLedger,
  );
  const accruedSen = summariseLedgerTotals(ledger ?? []).accrued;

  if (authLoading) {
    return (
      <PayoutsPageShell>
        <PayoutsHeroSkeleton />
      </PayoutsPageShell>
    );
  }

  if (!user) {
    return (
      <PayoutsPageShell>
        <PayoutsStateCard
          title={t.t('agent.payouts.signedOutTitle')}
          body={t.t('agent.payouts.signedOutBody')}
          ctaLabel={t.t('agent.payouts.signInCta')}
          ctaHref="/"
          ctaNote={t.t('agent.payouts.signInNote')}
        />
      </PayoutsPageShell>
    );
  }

  if (!agentId) {
    return (
      <PayoutsPageShell>
        <PayoutsStateCard
          title={t.t('agent.payouts.notAgentTitle')}
          body={t.t('agent.payouts.notAgentBody')}
          ctaLabel={t.t('agent.payouts.notAgentCta')}
          ctaHref={`https://${brand.domains.marketing}/become-an-agent`}
        />
      </PayoutsPageShell>
    );
  }

  if (loadError) {
    return (
      <PayoutsPageShell>
        <PayoutsStateCard
          title={t.t('agent.payouts.loadErrorTitle')}
          body={t.t('agent.loadError.body')}
          onRetry={() => {
            setLoadError(null);
            setRetryNonce((n) => n + 1);
          }}
          retryLabel={t.t('agent.loadError.retry')}
        />
      </PayoutsPageShell>
    );
  }

  if (loading || payouts === null) {
    return (
      <PayoutsPageShell>
        <PayoutsHeroSkeleton />
        <PayoutsListSkeleton />
      </PayoutsPageShell>
    );
  }

  if (payouts.length === 0) {
    return (
      <PayoutsPageShell>
        <PayoutsHero payouts={[]} />
        {/*
          Especially here. "No payouts yet" is the screen most likely to be read
          as "I have not been paid what I earned" — so this is exactly where the
          agent needs to see that they are, say, RM 16 short of the minimum and
          when the next run is. An empty state that only says "empty" is how a
          working agent decides the scheme does not pay.
        */}
        <NextPayoutCard agentId={agentId} accruedSen={accruedSen} />
        <PayoutsStateCard
          title={t.t('agent.payouts.emptyTitle')}
          body={t.t('agent.payouts.emptyBody')}
          ctaLabel={t.t('agent.payouts.emptyCta')}
          ctaHref="/earnings"
        />
      </PayoutsPageShell>
    );
  }

  return (
    <PayoutsPageShell>
      <PayoutsHero payouts={payouts} />
      {/*
        Answers the two questions the payout history cannot: when the next run
        is, and — when the balance is short — exactly how short. Sits above the
        history because "what happens next" beats "what already happened" for
        someone checking whether they have been paid.
      */}
      {agentId ? <NextPayoutCard agentId={agentId} accruedSen={accruedSen} /> : null}
      <PayoutsList payouts={payouts} csvFilenamePrefix="kilat-agent-payouts" />
    </PayoutsPageShell>
  );
}
